import { useState, useEffect } from "react";
import { Box, IconButton, CircularProgress, Typography } from "@mui/material";
import { MoreVert as MoreVertIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import ActionsMenu from "../../components/ActionsMenu";
import DeleteConfirmationDialog from "../../components/DeleteDialog";
import DataTable from "../../components/DataTable";
import VisibilityIcon from '@mui/icons-material/Visibility';

const DailyConsumption = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedConsumptionId, setSelectedConsumptionId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [consumptions, setConsumptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiUrl = "https://oven-backend.onrender.com";

  useEffect(() => {
    const fetchConsumptions = async () => {
      try {
        const response = await axios.get(`${apiUrl}/dailyConsumption`);
        setConsumptions(response.data);
      } catch (error) {
        console.error(t('error_fetching_consumptions'), error);
      } finally {
        setLoading(false);
      }
    };

    fetchConsumptions();
  }, [t]);

  const handleMenuOpen = (event, consumptionId) => {
    setAnchorEl(event.currentTarget);
    setSelectedConsumptionId(consumptionId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditConsumption = () => {
    navigate(`/updateDailyConsumption/${selectedConsumptionId}`);
    handleMenuClose();
  };

  const handleViewDetails = () => {
    navigate(`/dailyConsumptionDetails/${selectedConsumptionId}`);
    handleMenuClose();
  };

  const handleDeleteDialogOpen = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${apiUrl}/dailyConsumptions/${selectedConsumptionId}`);
      setConsumptions((prevConsumptions) => prevConsumptions.filter(item => item._id !== selectedConsumptionId));
    } catch (error) {
      console.error(t('error_deleting_consumption'), error);
    } finally {
      handleDeleteDialogClose();
    }
  };

  const columns = [
    { 
      field: "item", 
      headerName: t('item'), 
      flex: 1,
      valueGetter: (params) => params.row.item?.itemName || t('n/a') 
    },
    { 
      field: "recordedBy", 
      headerName: t('employee'), 
      flex: 1,
      valueGetter: (params) => params.row.recordedBy?.name || t('n/a') 
    },
    { 
      field: "consumptionDate", 
      headerName: t('date'), 
      flex: 1, 
      valueGetter: ({ value }) => value ? new Date(value).toLocaleDateString() : t('n/a') 
    },
    { 
      field: "notes", 
      headerName: t('status'), 
      flex: 1 
    },
    {
      field: "actions",
      headerName: t('actions'),
      flex: 0.5,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={(event) => handleMenuOpen(event, params.row._id)}>
            <MoreVertIcon />
          </IconButton>
          <ActionsMenu 
            anchorEl={anchorEl}
            handleMenuClose={handleMenuClose}
            menuItems={[
              { label: t('edit'), action: handleEditConsumption, icon: <EditIcon /> },
              { label: t('view_details'), action: handleViewDetails, icon: <VisibilityIcon />},
              { label: t('delete'), action: handleDeleteDialogOpen, icon: <DeleteIcon />, style: { color: "red" } }
            ]}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px" sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataTable 
          title={t('daily_consumption_list')}
          columns={columns}
          rows={consumptions} 
          getRowId={(row) => row._id}
        />
      )}

      <DeleteConfirmationDialog
        open={openDeleteDialog} 
        onClose={handleDeleteDialogClose} 
        onConfirm={handleConfirmDelete} 
        title={t('delete_consumption')}
        message={t('delete_consumption_confirmation')}
        confirmColor="error"
        cancelColor="secondary"
        size="large"
      />
    </Box>
  );
};

export default DailyConsumption;